/**
 * useChatGlobalRealtime — AppShell-scoped chat WS subscription.
 *
 * Mounted alongside `useGlobalRealtime` in `<AppShell>` (or `<Sidebar>`,
 * which owns the existing global WS). Listens for any `chat.*` frame
 * and invalidates the sidebar list cache so unread counts +
 * last-message previews refresh as messages land in conversations the
 * user isn't currently viewing.
 *
 * Phase 4b: extends the original 4a list with channel lifecycle events.
 * `chat.channel.archived` and `unarchived` additionally invalidate the
 * archived + browse caches since archive flips the channel between
 * those lists. Member join/leave invalidates browse (a public channel
 * the caller just got added to disappears from browse).
 *
 * NB: this hook does NOT open its own WebSocket. It subscribes to the
 * shared connection owned by `<WebSocketProvider>` via
 * `useWebSocketSubscription`. Other consumers (`useGlobalRealtime`,
 * `useChatRealtime`, `useRealtimeBoard`) share the same socket;
 * TanStack Query coalesces any overlapping cache invalidations.
 *
 * It also owns chat's RECONNECT recovery — see the `wasOpenRef` effect
 * at the bottom. Frames that arrive while the socket is down are lost,
 * so the sidebar list is resynced once on every true reconnect. Board,
 * wiki and dashboard have the same gap and are NOT covered here.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChatConversationSummary, TipTapJsonContent } from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'
import { richTextToPlainText } from '@/editor/richtext-value'
import { useMe } from '@/features/me/use-me'
import { initialsIconDataUrl } from '@/features/notifications/lib/initials-icon'
import { stripMarkdown } from '@/features/notifications/lib/strip-markdown'
import { useBrowserNotifications } from '@/features/notifications/use-browser-notifications'
import { NOTIFICATIONS_QUERY_KEY } from '@/features/notifications/use-notifications'
import { useResyncOnReconnect } from '@/lib/use-resync-on-reconnect'
import type { ServerMessage, WebSocketStatus } from '@/lib/use-websocket'
import { useWebSocketSubscription } from '@/lib/use-websocket-subscription'

import { ARCHIVED_CHANNELS_QUERY_KEY } from '../hooks/use-archived-channels'
import { BROWSABLE_CHANNELS_QUERY_KEY } from '../hooks/use-browsable-channels'
import { CONVERSATIONS_QUERY_KEY } from '../hooks/use-conversations'

const NOTIFICATION_ICON_URL = '/favicon.svg'
const NOTIFICATION_BODY_MAX = 100

const CHAT_LIST_INVALIDATING_TYPES = new Set([
  'chat.message.created',
  'chat.message.deleted',
  'chat.channel.member_joined',
  'chat.channel.member_left',
  'chat.channel.updated',
  'chat.channel.archived',
  'chat.channel.unarchived',
])

const ARCHIVE_FLIPPING_TYPES = new Set(['chat.channel.archived', 'chat.channel.unarchived'])
const MEMBERSHIP_TYPES = new Set([
  'chat.channel.member_joined',
  'chat.channel.member_left',
  'chat.channel.archived',
  'chat.channel.unarchived',
])

export interface UseChatGlobalRealtimeResult {
  wsStatus: WebSocketStatus
}

interface ChatMessageCreatedPayload {
  conversationId?: string
  messageId?: string
  userId?: string
  parentMessageId?: string | null
  /**
   * A TipTap doc OBJECT when the sender wrote a `bodyFormat='json'`
   * message (ADR-039 Phase 3), a markdown string on legacy rows.
   */
  body?: string | TipTapJsonContent
  authorName?: string
  authorAvatarUrl?: string | null
  conversationType?: 'dm' | 'channel'
  channelName?: string
}

export function useChatGlobalRealtime(): UseChatGlobalRealtimeResult {
  const queryClient = useQueryClient()
  // Token + lifecycle owned by `<WebSocketProvider>`; this hook is a
  // pure subscriber over the shared connection.
  const me = useMe()
  const currentUserId = me.data?.user.id ?? null
  const browserNotif = useBrowserNotifications()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useTranslation('notifications')
  const { t: tChat } = useTranslation('chat')

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      // Phase 4d — `chat.mention.created` ticks the navbar bell. We only
      // care about frames targeting US (the BE broadcasts one frame per
      // mentioned user; payload.mentionedUserId === currentUserId means
      // the mention is for this tab). Mismatched frames noop so a busy
      // channel of cross-mentions doesn't churn the notifications cache
      // unnecessarily.
      if (message.type === 'chat.mention.created') {
        const payload = (message as { payload?: unknown }).payload
        const mentionedUserId =
          typeof payload === 'object' && payload !== null && 'mentionedUserId' in payload
            ? (payload as { mentionedUserId?: string }).mentionedUserId
            : undefined
        if (!currentUserId || mentionedUserId !== currentUserId) return
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
        return
      }

      // Phase 2 — fire a browser notification on every new
      // top-level chat message addressed to us. Suppressed when the
      // user is actively viewing the conversation, has muted it, or
      // wrote it themselves. The `notify()` helper additionally
      // short-circuits on permission / enabled / tab-focus state.
      if (message.type === 'chat.message.created') {
        const rawPayload = (message as { payload?: unknown }).payload
        const payload: ChatMessageCreatedPayload =
          typeof rawPayload === 'object' && rawPayload !== null
            ? (rawPayload as ChatMessageCreatedPayload)
            : {}

        if (currentUserId && payload.userId === currentUserId) {
          // self-author: do nothing, but let cache-invalidation run.
        } else if (payload.parentMessageId) {
          // Thread replies don't fire OS notifications today —
          // legacy parity (notifications were top-level only).
        } else if (payload.conversationId) {
          const conversations =
            queryClient.getQueryData<ChatConversationSummary[]>(CONVERSATIONS_QUERY_KEY)
          const convo = conversations?.find((c) => c.id === payload.conversationId)
          const muted = convo?.notificationsMuted ?? false
          const onThisConversation =
            browserNotif.isTabFocused && location.pathname === `/messages/${payload.conversationId}`

          if (!muted && !onThisConversation) {
            const conversationType = convo?.type ?? payload.conversationType
            const channelName = convo && convo.type === 'channel' ? convo.name : payload.channelName
            const senderName =
              payload.authorName ??
              (convo && convo.type === 'dm' ? (convo.peer?.name ?? null) : null) ??
              t('rendering.fallbackActor')
            const title =
              conversationType === 'channel' && channelName
                ? t('browser.chat.titleChannel', { name: senderName, channelName })
                : senderName
            // Branch on the wire body's runtime shape: JSON docs get the
            // plain-text doc walk (mentions → @Label, #Label); strings
            // keep the markdown strip.
            const rawBody =
              typeof payload.body === 'string'
                ? stripMarkdown(payload.body)
                : payload.body != null
                  ? richTextToPlainText(payload.body)
                  : ''
            const trimmedBody =
              rawBody.length > NOTIFICATION_BODY_MAX
                ? `${rawBody.slice(0, NOTIFICATION_BODY_MAX)}…`
                : rawBody
            // Chrome on macOS renders the page origin ("localhost:5173")
            // when `body` is empty. The BE's chat broadcast is id-only
            // (PII rule), so we fall back to a translated placeholder
            // until / unless the frame carries a preview.
            const body = trimmedBody || t('browser.chat.bodyPlaceholder')
            const peerAvatar = convo && convo.type === 'dm' ? (convo.peer?.avatarUrl ?? null) : null
            // When the sender has no avatar_url, fall back to a generated
            // initials data URL that matches the in-app <Avatar> palette
            // + initials, instead of the 3mote logo.
            const hasRealAvatar = Boolean(payload.authorAvatarUrl ?? peerAvatar)
            const initialsIcon = hasRealAvatar
              ? null
              : (initialsIconDataUrl(senderName, payload.userId ?? senderName) ?? null)
            const conversationId = payload.conversationId
            browserNotif.notify({
              title,
              body,
              icon: payload.authorAvatarUrl ?? peerAvatar ?? initialsIcon ?? NOTIFICATION_ICON_URL,
              tag: `chat-${conversationId}`,
              onClick: () => {
                navigate({
                  to: '/messages/$conversationId',
                  params: { conversationId },
                })
              },
            })
          }
        }
      }

      if (!CHAT_LIST_INVALIDATING_TYPES.has(message.type)) return
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
      if (ARCHIVE_FLIPPING_TYPES.has(message.type)) {
        queryClient.invalidateQueries({ queryKey: ARCHIVED_CHANNELS_QUERY_KEY })
      }
      if (MEMBERSHIP_TYPES.has(message.type)) {
        queryClient.invalidateQueries({ queryKey: BROWSABLE_CHANNELS_QUERY_KEY })
      }
      // Channel-lifecycle frames write a `kind='system'` message into the
      // timeline. Invalidate `['conversation-messages', X]` so the open
      // channel's timeline refetches and the system message lands in view
      // for every participant (not just the actor's tab).
      const channelLifecycleTypes = new Set([
        'chat.channel.member_joined',
        'chat.channel.member_left',
        'chat.channel.updated',
        'chat.channel.archived',
        'chat.channel.unarchived',
      ])
      if (channelLifecycleTypes.has(message.type)) {
        const cid = (message as { payload?: { conversationId?: string } }).payload?.conversationId
        if (cid) {
          queryClient.invalidateQueries({ queryKey: ['conversation-messages', cid] })
        }
      }

      // Browser notification: fire when the current user was added to OR
      // removed from a channel by someone else (payload.userId is the
      // TARGET — counter-intuitive but matches the existing broadcast
      // shape; payload.actorId is who acted). Skip self-acts (joining
      // via Browse, leaving via Leave) — the user just initiated them.
      if (
        message.type === 'chat.channel.member_joined' ||
        message.type === 'chat.channel.member_left'
      ) {
        const payload = (
          message as {
            payload?: {
              conversationId?: string
              userId?: string
              actorId?: string
              channelName?: string | null
              actorName?: string | null
            }
          }
        ).payload
        if (
          currentUserId &&
          payload?.userId === currentUserId &&
          payload.actorId !== currentUserId &&
          payload.conversationId
        ) {
          const isJoin = message.type === 'chat.channel.member_joined'
          const senderName = payload.actorName ?? t('rendering.fallbackActor')
          const channelName = payload.channelName ?? ''
          const titleKeyChannel = isJoin
            ? 'browser.channelAdd.titleChannel'
            : 'browser.channelRemove.titleChannel'
          const titleKeyGeneric = isJoin
            ? 'browser.channelAdd.titleGeneric'
            : 'browser.channelRemove.titleGeneric'
          const bodyKey = isJoin ? 'browser.channelAdd.body' : 'browser.channelRemove.body'
          const title = channelName
            ? t(titleKeyChannel, { actor: senderName, channelName })
            : t(titleKeyGeneric, { actor: senderName })
          const initialsIcon =
            initialsIconDataUrl(senderName, payload.actorId ?? senderName) ?? null
          const conversationId = payload.conversationId
          const tagPrefix = isJoin ? 'channel-add' : 'channel-remove'
          browserNotif.notify({
            title,
            body: t(bodyKey),
            icon: initialsIcon ?? NOTIFICATION_ICON_URL,
            tag: `${tagPrefix}-${conversationId}`,
            onClick: () => {
              // For a removal the channel is no longer accessible, so
              // route to the messages root instead of the conversation.
              if (isJoin) {
                navigate({
                  to: '/messages/$conversationId',
                  params: { conversationId },
                })
              } else {
                navigate({ to: '/messages' })
              }
            },
          })

          // Auto-route away from the conversation view when the
          // current user was just removed from the channel they're
          // actively viewing. The browser notification alone covers
          // the "they noticed and clicked" path; this covers the
          // "they're already on the channel" path (most common), so
          // the just-removed user doesn't keep reading + typing into
          // a channel they no longer have access to.
          //
          // `startsWith('/messages/<id>')` catches the channel route
          // AND the nested thread route (`/messages/<id>/t/<msg>`).
          // Self-removal (the user leaves on their own) is excluded
          // because the leave-mutation handler already routes them
          // away — only "removed by another user" lands here.
          if (!isJoin && location.pathname.startsWith(`/messages/${conversationId}`)) {
            navigate({ to: '/messages' })
            toast.info(
              channelName
                ? tChat('channelSettingsModal.members.removedFromChannelToast', { channelName })
                : tChat('channelSettingsModal.members.removedFromChannelToastGeneric'),
            )
          }
        }
      }
    },
    [queryClient, currentUserId, browserNotif, location.pathname, navigate, t, tChat, toast],
  )

  const status = useWebSocketSubscription(handleMessage)

  // Resync the sidebar list after a reconnect — chat frames that landed
  // while the socket was down are gone, so unread counts and last-message
  // previews are stale until something refetches them. Nothing else
  // covers this gap: `useConversations` deliberately sets
  // `refetchOnWindowFocus: false`, so refocusing the tab doesn't rescue
  // chat the way it does notifications. See `useResyncOnReconnect` for
  // why this fires only on a TRUE reconnect and why it must never grow
  // into a keep-alive.
  useResyncOnReconnect(status, () => {
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
  })

  return { wsStatus: status }
}
